import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';

// Types
export interface Channel {
  id: string;
  server_id: string;
  name: string;
  type: 'text' | 'voice';
  created_at: string;
}

// Channel Message Types
export interface ChannelMessage {
  id: string;
  channel_id: string;
  user_id: string;
  content: string;
  created_at: string;
  reply_to_id?: string;
  reply_to_user_id?: string;
  profiles?: {
    username: string;
    artist_types: string[];
    avatar_url?: string;
  };
  reactions?: {
    emoji: string;
    count: number;
    users: string[];
  }[];
}

// Channel Message Functions
export function subscribeToMessages(
  channelName: string,
  callback: (message: ChannelMessage, eventType: 'INSERT' | 'DELETE') => void
): RealtimeChannel {
  return supabase.channel('messages')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      },
      async (payload) => {
        // Fetch complete message data
        const { data: message } = await supabase
          .from('messages')
          .select(`
            *,
            profiles!messages_user_id_fkey (
              username,
              avatar_url,
              artist_types
            )
          `)
          .eq('id', payload.new.id)
          .single();
        
        if (message) {
          callback(message as ChannelMessage, 'INSERT');
        }
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'messages'
      },
      (payload) => {
        callback(payload.old as ChannelMessage, 'DELETE');
      }
    )
    .subscribe();
}

export async function getMessages(channelName: string): Promise<ChannelMessage[]> {
  const channel = await getChannelByName(channelName);
  if (!channel) throw new Error('Channel not found');

  const { data: messages, error } = await supabase
    .from('messages')
    .select(`
      *,
      profiles!messages_user_id_fkey (
        username,
        avatar_url,
        artist_types
      )
    `)
    .eq('channel_id', channel.id)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return messages || [];
}

export async function sendChannelMessage(channelName: string, content: string, replyToId?: string): Promise<ChannelMessage> {
  const channel = await getChannelByName(channelName);
  if (!channel) throw new Error('Channel not found');

  let replyToUserId: string | undefined;
  if (replyToId) {
    const { data: replyMessage } = await supabase
      .from('messages')
      .select('user_id')
      .eq('id', replyToId)
      .single();
    replyToUserId = replyMessage?.user_id;
  }

  const { data: message, error } = await supabase
    .from('messages')
    .insert([{
      channel_id: channel.id,
      content,
      user_id: (await supabase.auth.getUser()).data.user?.id,
      reply_to_id: replyToId,
      reply_to_user_id: replyToUserId
    }])
    .select(`
      *,
      profiles!messages_user_id_fkey (
        username,
        avatar_url,
        artist_types
      )
    `)
    .single();

  if (error) throw error;
  if (!message) throw new Error('Failed to create message');

  return message;
}

// Direct Message Types
export interface DirectMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
  read: boolean;
  profiles?: {
    username: string;
    avatar_url: string | null;
    artist_types: string[];
  };
}

export async function markMessagesAsRead(senderId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  try {
    // Begin by marking all unread messages from this sender as read
    const { data: messages, error: messageError } = await supabase
      .from('direct_messages')
      .update({ read: true })
      .eq('sender_id', senderId)
      .eq('receiver_id', user.data.user.id)
      .eq('read', false)
      .select();

    if (messageError) throw messageError;

    // Then mark related notifications as read
    const { error: notifError } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.data.user.id)
      .eq('sender_id', senderId)
      .eq('type', 'message')
      .eq('read', false);

    if (notifError) throw notifError;

    return messages || [];
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw new Error('Failed to mark messages as read');
  }
}

export async function getConversations(): Promise<Conversation[]> {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  // Get all messages to/from the user
  const { data: messages, error: messagesError } = await supabase
    .from('direct_messages')
    .select(`
      *,
      profiles!direct_messages_sender_id_fkey (
        username,
        avatar_url
      )
    `)
    .or(`sender_id.eq.${user.data.user.id},receiver_id.eq.${user.data.user.id}`)
    .order('created_at', { ascending: false });

  if (messagesError) throw messagesError;

  // Group messages by conversation partner
  const conversations = new Map<string, Conversation>();
  
  messages.forEach(message => {
    const partnerId = message.sender_id === user.data.user.id 
      ? message.receiver_id 
      : message.sender_id;
    
    if (!conversations.has(partnerId)) {
      conversations.set(partnerId, {
        user_id: partnerId,
        username: message.profiles.username,
        avatar_url: message.profiles.avatar_url,
        last_message: message.content,
        unread_count: message.receiver_id === user.data.user.id && !message.read ? 1 : 0
      });
    } else if (!message.read && message.receiver_id === user.data.user.id) {
      const conv = conversations.get(partnerId)!;
      conv.unread_count++;
    }
  });

  return Array.from(conversations.values());
}

export async function getDirectMessages(userId: string): Promise<DirectMessage[]> {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  const { data: messages, error } = await supabase
    .from('direct_messages')
    .select(`
      *,
      profiles!direct_messages_sender_id_fkey (
        username,
        avatar_url,
        artist_types
      )
    `)
    .or(`and(sender_id.eq.${user.data.user.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${user.data.user.id})`)
    .order('created_at', { ascending: true });

  if (error) throw error;
  if (!messages) return [];

  return messages;
}

export async function deleteMessage(messageId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  // First verify that the user owns this message
  const { data: message, error: fetchError } = await supabase
    .from('direct_messages')
    .select('sender_id')
    .eq('id', messageId)
    .single();

  if (fetchError) {
    console.error('Error fetching message:', fetchError);
    throw fetchError;
  }

  if (!message) {
    throw new Error('Message not found');
  }

  if (message.sender_id !== user.data.user.id) {
    throw new Error('Not authorized to delete this message');
  }

  // Then delete the message
  const { error: deleteError } = await supabase
    .from('direct_messages')
    .delete()
    .eq('id', messageId)
    .eq('sender_id', user.data.user.id); // Additional safety check

  if (deleteError) {
    console.error('Error deleting message:', deleteError);
    throw deleteError;
  }
}

export async function sendDirectMessage(receiverId: string, content: string): Promise<DirectMessage> {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  const { data: message, error } = await supabase
    .from('direct_messages')
    .insert([{
      sender_id: user.data.user.id,
      receiver_id: receiverId,
      content
    }])
    .select()
    .single();

  if (error) throw error;
  return message;
}

export function subscribeToDirectMessages(
  userId: string,
  callback: (message: DirectMessage) => void
): RealtimeChannel {
  return supabase.channel('direct_messages')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `receiver_id=eq.${userId}`
      },
      async (payload) => {
        // Fetch complete message data including profile
        const { data: message } = await supabase
          .from('direct_messages')
          .select('*, profiles!direct_messages_sender_id_fkey(username, avatar_url, artist_types)')
          .eq('id', payload.new.id)
          .single();
        
        if (message) callback(message as DirectMessage);
      }
    )
    .subscribe();
}

export interface Conversation {
  user_id: string;
  username: string;
  avatar_url: string | null;
  last_message: string | null;
  unread_count: number;
}

// Helper Functions
async function getProfile(userId: string) {
  const { data } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  return data;
}

// Get channel by name
export async function getChannelByName(channelName: string): Promise<Channel | null> {
  const { data: channel, error } = await supabase
    .from('channels')
    .select('*')
    .eq('name', channelName)
    .single();

  if (error) {
    console.error('Error fetching channel:', error);
    return null;
  }

  return channel;
}

// Process reactions
export function processReactions(reactions: { emoji: string; user_id: string }[]) {
  const reactionMap = new Map<string, { count: number; users: string[] }>();

  reactions.forEach(reaction => {
    if (!reactionMap.has(reaction.emoji)) {
      reactionMap.set(reaction.emoji, { count: 0, users: [] });
    }
    const reactionData = reactionMap.get(reaction.emoji)!;
    reactionData.count++;
    reactionData.users.push(reaction.user_id);
  });

  return Array.from(reactionMap.entries()).map(([emoji, { count, users }]) => ({
    emoji,
    count,
    users
  }));
}